import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSpinner, faArrowLeft, faExclamationTriangle, faSearch } from '@fortawesome/free-solid-svg-icons';
import { apiJson } from '../apiClient';
import { Panel } from '../../crm/components/ui/Panel';
import { KpiCard, KpiGrid } from '../../crm/components/ui/KpiCard';
import { Badge } from '../../crm/components/ui/Badge';
import { Button } from '../../crm/components/ui/Button';
import { Sheet } from '../../crm/components/ui/Sheet';
import { EmptyState } from '../../crm/components/ui/EmptyState';

const apiEst = (path, opts) => apiJson(`/estoque${path}`, opts);

const fmtBRL = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
};
const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '—');
const fmtNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : v;
};

const TIPOS_MOVIMENTACAO_LABEL = {
  ENTRADA: 'Entrada', SAIDA: 'Saída', TRANSFERENCIA: 'Transferência',
  AJUSTE_POSITIVO: 'Ajuste (+)', AJUSTE_NEGATIVO: 'Ajuste (-)',
};

// ─── Lista + KPIs ────────────────────────────────────────────────────────
function ListaEstoque({ onAbrirProduto, showToast }) {
  const [dashboard, setDashboard] = useState(null);
  const [produtos, setProdutos] = useState(null);
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState('');
  const [apenasBaixo, setApenasBaixo] = useState(false);
  const [showNovoProduto, setShowNovoProduto] = useState(false);
  const [showNovaMov, setShowNovaMov] = useState(false);
  const [showNovaCompra, setShowNovaCompra] = useState(false);
  const [produtoParaComprar, setProdutoParaComprar] = useState(null);
  const [localizacoes, setLocalizacoes] = useState([]);

  const carregarDashboard = useCallback(async () => {
    const result = await apiEst('/dashboard');
    if (result.status === 'ok') setDashboard(result);
  }, []);

  const carregarProdutos = useCallback(async () => {
    const params = new URLSearchParams();
    if (busca) params.set('busca', busca);
    if (categoria) params.set('categoria', categoria);
    if (apenasBaixo) params.set('apenas_estoque_baixo', 'true');
    const result = await apiEst(`/produtos?${params}`);
    if (result.status === 'ok') setProdutos(result.lista);
  }, [busca, categoria, apenasBaixo]);

  const carregarLocalizacoes = useCallback(async () => {
    const result = await apiEst('/localizacoes');
    if (result.status === 'ok') setLocalizacoes(result.lista);
  }, []);

  useEffect(() => { carregarDashboard(); carregarLocalizacoes(); }, [carregarDashboard, carregarLocalizacoes]);
  useEffect(() => { carregarProdutos(); }, [carregarProdutos]);

  const recarregarTudo = () => { carregarDashboard(); carregarProdutos(); };
  const categorias = [...new Set((produtos || []).map((p) => p.categoria).filter(Boolean))];

  return (
    <div>
      {dashboard && (
        <KpiGrid>
          <KpiCard label="Itens cadastrados" value={dashboard.itens_cadastrados} />
          <KpiCard label="Valor em estoque" value={fmtBRL(dashboard.valor_em_estoque)} />
          <KpiCard label="Estoque baixo" value={dashboard.estoque_baixo} tone={dashboard.estoque_baixo > 0 ? 'warning' : undefined} />
          <KpiCard label="Movimentações hoje" value={dashboard.movimentacoes_hoje} />
        </KpiGrid>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', margin: 'var(--crm-space-4) 0', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
          <div className="crm-search-input" style={{ minWidth: '200px' }}>
            <FontAwesomeIcon icon={faSearch} />
            <input className="crm-input" placeholder="Buscar produto..." value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
          <select className="crm-select" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
            <option value="">Categoria: Todas</option>
            {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Button variant={apenasBaixo ? 'primary' : 'outline'} size="sm" icon={faExclamationTriangle} onClick={() => setApenasBaixo(!apenasBaixo)}>Estoque baixo</Button>
        </div>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
          <Button variant="outline" icon={faPlus} onClick={() => setShowNovoProduto(true)}>Produto</Button>
          <Button variant="outline" icon={faPlus} onClick={() => setShowNovaCompra(true)}>Compra</Button>
          <Button variant="primary" icon={faPlus} onClick={() => setShowNovaMov(true)}>Movimentação</Button>
        </div>
      </div>

      <Panel flush>
        {produtos === null ? (
          <EmptyState title="Carregando..." />
        ) : produtos.length === 0 ? (
          <EmptyState title="Nenhum produto cadastrado" description="Cadastre seu primeiro produto para começar a controlar o estoque." actions={<Button variant="primary" icon={faPlus} onClick={() => setShowNovoProduto(true)}>Produto</Button>} />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th>Produto</th><th>Categoria</th><th>Controle</th><th>Saldo</th><th>Mínimo</th><th /><th /></tr></thead>
              <tbody>
                {produtos.map((p) => (
                  <tr key={p.id} className="clickable" onClick={() => onAbrirProduto(p.id)}>
                    <td>
                      <div className="crm-table-primary">{p.nome}</div>
                      {p.sku && <div className="crm-table-sub">SKU: {p.sku}</div>}
                    </td>
                    <td>{p.categoria || '—'}</td>
                    <td>{p.tipo_controle === 'RASTREAVEL' ? 'Rastreável' : 'Quantidade'}</td>
                    <td className="crm-table-primary">{fmtNum(p.saldo_total)} {p.unidade}</td>
                    <td>{fmtNum(p.estoque_minimo)}</td>
                    <td>{p.estoque_baixo && <Badge tone="danger">Baixo</Badge>}</td>
                    <td className="crm-table-actions-cell" onClick={(e) => e.stopPropagation()}>
                      {p.estoque_baixo && (
                        <Button size="sm" variant="outline" icon={faPlus} onClick={() => setProdutoParaComprar(p)}>Comprar</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {showNovoProduto && <NovoProdutoModal onFechar={() => setShowNovoProduto(false)} onSalvo={() => { setShowNovoProduto(false); recarregarTudo(); }} showToast={showToast} />}
      {showNovaMov && (
        <NovaMovimentacaoModal localizacoes={localizacoes} produtosDisponiveis={produtos || []}
          onFechar={() => setShowNovaMov(false)} onSalvo={() => { setShowNovaMov(false); recarregarTudo(); }} showToast={showToast} onLocalizacaoCriada={carregarLocalizacoes} />
      )}
      {showNovaCompra && (
        <NovaCompraModal localizacoes={localizacoes} produtosDisponiveis={produtos || []}
          onFechar={() => setShowNovaCompra(false)} onSalvo={() => { setShowNovaCompra(false); recarregarTudo(); }} showToast={showToast}
          onLocalizacaoCriada={carregarLocalizacoes} onProdutoCriado={carregarProdutos} />
      )}
      {produtoParaComprar && (
        <NovaCompraModal localizacoes={localizacoes} produtosDisponiveis={produtos || []} produtoInicial={produtoParaComprar}
          onFechar={() => setProdutoParaComprar(null)} onSalvo={() => { setProdutoParaComprar(null); recarregarTudo(); }} showToast={showToast}
          onLocalizacaoCriada={carregarLocalizacoes} onProdutoCriado={carregarProdutos} />
      )}
    </div>
  );
}

// ─── Modal: Novo Produto ─────────────────────────────────────────────────
function NovoProdutoModal({ onFechar, onSalvo, showToast }) {
  const [form, setForm] = useState({ nome: '', sku: '', categoria: '', unidade: 'UN', custo: '', estoque_minimo: '0', tipo_controle: 'QUANTIDADE' });
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    if (!form.nome.trim()) { showToast?.('Informe o nome do produto', 'error'); return; }
    setSalvando(true);
    const result = await apiEst('/produtos', { method: 'POST', body: JSON.stringify(form) });
    setSalvando(false);
    if (result.status === 'ok') { showToast?.('Produto criado!', 'success'); onSalvo(); }
    else showToast?.(result.mensagem || 'Erro ao criar produto', 'error');
  };

  return (
    <Sheet open onClose={onFechar} title="Novo Produto" footer={<Button variant="primary" loading={salvando} onClick={salvar}>Criar Produto</Button>}>
      <div className="crm-form-field"><label className="crm-form-label">Nome</label><input className="crm-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
      <div className="crm-form-field"><label className="crm-form-label">SKU (opcional)</label><input className="crm-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
      <div className="crm-form-field"><label className="crm-form-label">Categoria</label><input className="crm-input" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} /></div>
      <div className="crm-form-field"><label className="crm-form-label">Unidade</label><input className="crm-input" value={form.unidade} onChange={(e) => setForm({ ...form, unidade: e.target.value })} /></div>
      <div className="crm-form-field"><label className="crm-form-label">Custo unitário</label><input className="crm-input" type="number" step="0.01" value={form.custo} onChange={(e) => setForm({ ...form, custo: e.target.value })} /></div>
      <div className="crm-form-field"><label className="crm-form-label">Estoque mínimo</label><input className="crm-input" type="number" step="0.01" value={form.estoque_minimo} onChange={(e) => setForm({ ...form, estoque_minimo: e.target.value })} /></div>
      <div className="crm-form-field">
        <label className="crm-form-label">Tipo de controle</label>
        <select className="crm-select" value={form.tipo_controle} onChange={(e) => setForm({ ...form, tipo_controle: e.target.value })}>
          <option value="QUANTIDADE">Por quantidade</option>
          <option value="RASTREAVEL">Rastreável individualmente</option>
        </select>
      </div>
    </Sheet>
  );
}

function NovaLocalizacaoInline({ onCriada, showToast }) {
  const [mostrar, setMostrar] = useState(false);
  const [nome, setNome] = useState('');
  const criar = async () => {
    if (!nome.trim()) return;
    const r = await apiEst('/localizacoes', { method: 'POST', body: JSON.stringify({ nome }) });
    if (r.status === 'ok') { showToast?.('Local criado!', 'success'); setMostrar(false); setNome(''); onCriada(r.localizacao); }
    else showToast?.(r.mensagem || 'Erro ao criar local', 'error');
  };
  if (!mostrar) return <Button size="sm" variant="outline" onClick={() => setMostrar(true)}>+ Criar local</Button>;
  return (
    <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
      <input className="crm-input" placeholder="Nome do local" value={nome} onChange={(e) => setNome(e.target.value)} />
      <Button size="sm" variant="primary" onClick={criar}>Salvar</Button>
    </div>
  );
}

// ─── Modal: Nova Movimentação (§11 — Entrada/Saída/Transferência/Ajuste
// unificados em um só fluxo) ──────────────────────────────────────────────
function NovaMovimentacaoModal({ localizacoes, produtosDisponiveis, onFechar, onSalvo, showToast, onLocalizacaoCriada }) {
  const [tipo, setTipo] = useState('ENTRADA');
  const [locs, setLocs] = useState(localizacoes);
  const [form, setForm] = useState({ produto_id: '', localizacao_origem_id: '', localizacao_destino_id: '', quantidade: '', observacao: '' });
  const [salvando, setSalvando] = useState(false);

  const precisaOrigem = ['SAIDA', 'TRANSFERENCIA', 'AJUSTE_NEGATIVO'].includes(tipo);
  const precisaDestino = ['ENTRADA', 'TRANSFERENCIA', 'AJUSTE_POSITIVO'].includes(tipo);

  const salvar = async () => {
    if (!form.produto_id || !form.quantidade) { showToast?.('Selecione o produto e informe a quantidade', 'error'); return; }
    if (precisaOrigem && !form.localizacao_origem_id) { showToast?.('Selecione a localização de origem', 'error'); return; }
    if (precisaDestino && !form.localizacao_destino_id) { showToast?.('Selecione a localização de destino', 'error'); return; }
    setSalvando(true);
    const body = { tipo, produto_id: form.produto_id, quantidade: form.quantidade, observacao: form.observacao };
    if (precisaOrigem) body.localizacao_origem_id = form.localizacao_origem_id;
    if (precisaDestino) body.localizacao_destino_id = form.localizacao_destino_id;
    const result = await apiEst('/movimentacoes', { method: 'POST', body: JSON.stringify(body) });
    setSalvando(false);
    if (result.status === 'ok') { showToast?.(`${TIPOS_MOVIMENTACAO_LABEL[tipo]} registrada!`, 'success'); onSalvo(); }
    else showToast?.(result.mensagem || 'Erro ao registrar movimentação', 'error');
  };

  return (
    <Sheet open onClose={onFechar} title="Nova Movimentação" footer={<Button variant="primary" loading={salvando} onClick={salvar}>Registrar</Button>}>
      <div className="crm-form-field">
        <label className="crm-form-label">Tipo</label>
        <select className="crm-select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
          {Object.entries(TIPOS_MOVIMENTACAO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div className="crm-form-field">
        <label className="crm-form-label">Produto</label>
        <select className="crm-select" value={form.produto_id} onChange={(e) => setForm({ ...form, produto_id: e.target.value })}>
          <option value="">Selecione...</option>
          {produtosDisponiveis.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
        </select>
      </div>
      {precisaOrigem && (
        <div className="crm-form-field">
          <label className="crm-form-label">De</label>
          <select className="crm-select" value={form.localizacao_origem_id} onChange={(e) => setForm({ ...form, localizacao_origem_id: e.target.value })}>
            <option value="">Selecione...</option>
            {locs.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
          </select>
        </div>
      )}
      {precisaDestino && (
        <div className="crm-form-field">
          <label className="crm-form-label">Para</label>
          <select className="crm-select" value={form.localizacao_destino_id} onChange={(e) => setForm({ ...form, localizacao_destino_id: e.target.value })}>
            <option value="">Selecione...</option>
            {locs.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
          </select>
        </div>
      )}
      <NovaLocalizacaoInline showToast={showToast} onCriada={(l) => { setLocs([...locs, l]); onLocalizacaoCriada?.(); }} />
      <div className="crm-form-field"><label className="crm-form-label">Quantidade</label><input className="crm-input" type="number" step="0.01" value={form.quantidade} onChange={(e) => setForm({ ...form, quantidade: e.target.value })} /></div>
      <div className="crm-form-field"><label className="crm-form-label">Observação</label><input className="crm-input" value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} /></div>
    </Sheet>
  );
}

// ─── Modal: Nova Compra ──────────────────────────────────────────────────
function NovaCompraModal({ localizacoes, produtosDisponiveis, onFechar, onSalvo, showToast, onLocalizacaoCriada, onProdutoCriado, produtoInicial }) {
  const [fornecedores, setFornecedores] = useState([]);
  const [locs, setLocs] = useState(localizacoes);
  const [produtos, setProdutos] = useState(produtosDisponiveis);
  const [form, setForm] = useState({ fornecedor_id: '', localizacao_destino_id: '', documento_nf: '', gerar_despesa: false });
  const [itens, setItens] = useState(
    produtoInicial
      ? [{ produto_id: produtoInicial.id, quantidade: String(Math.max(1, Math.ceil((parseFloat(produtoInicial.estoque_minimo) || 0) - (parseFloat(produtoInicial.saldo_total) || 0)))), custo_unitario: '' }]
      : [{ produto_id: '', quantidade: '', custo_unitario: '' }],
  );
  const [salvando, setSalvando] = useState(false);
  const [showNovoFornecedor, setShowNovoFornecedor] = useState(false);
  const [nomeFornecedor, setNomeFornecedor] = useState('');
  const [criandoProdutoParaLinha, setCriandoProdutoParaLinha] = useState(null);
  const [nomeProduto, setNomeProduto] = useState('');

  useEffect(() => { apiEst('/fornecedores').then((r) => { if (r.status === 'ok') setFornecedores(r.lista); }); }, []);

  const criarProduto = async (indiceItem) => {
    if (!nomeProduto.trim()) return;
    const r = await apiEst('/produtos', { method: 'POST', body: JSON.stringify({ nome: nomeProduto }) });
    if (r.status === 'ok') {
      showToast?.('Produto criado!', 'success');
      setProdutos([...produtos, r.produto]);
      atualizarItem(indiceItem, 'produto_id', r.produto.id);
      setCriandoProdutoParaLinha(null); setNomeProduto('');
      onProdutoCriado?.();
    } else showToast?.(r.mensagem || 'Erro ao criar produto', 'error');
  };

  const criarFornecedor = async () => {
    if (!nomeFornecedor.trim()) return;
    const r = await apiEst('/fornecedores', { method: 'POST', body: JSON.stringify({ nome: nomeFornecedor }) });
    if (r.status === 'ok') { showToast?.('Fornecedor criado!', 'success'); setFornecedores([...fornecedores, r.fornecedor]); setForm({ ...form, fornecedor_id: r.fornecedor.id }); setShowNovoFornecedor(false); setNomeFornecedor(''); }
    else showToast?.(r.mensagem || 'Erro ao criar fornecedor', 'error');
  };

  const atualizarItem = (i, campo, valor) => { const novos = [...itens]; novos[i] = { ...novos[i], [campo]: valor }; setItens(novos); };
  const adicionarItem = () => setItens([...itens, { produto_id: '', quantidade: '', custo_unitario: '' }]);
  const removerItem = (i) => setItens(itens.filter((_, idx) => idx !== i));

  const total = itens.reduce((acc, it) => acc + (parseFloat(it.quantidade) || 0) * (parseFloat(it.custo_unitario) || 0), 0);

  const salvar = async () => {
    if (!form.localizacao_destino_id) { showToast?.('Selecione a localização de destino', 'error'); return; }
    const itensValidos = itens.filter((i) => i.produto_id && i.quantidade && i.custo_unitario);
    if (itensValidos.length === 0) { showToast?.('Informe ao menos um item válido', 'error'); return; }
    setSalvando(true);
    const result = await apiEst('/compras', { method: 'POST', body: JSON.stringify({ ...form, itens: itensValidos }) });
    setSalvando(false);
    if (result.status === 'ok') { showToast?.('Compra registrada!', 'success'); onSalvo(); }
    else showToast?.(result.mensagem || 'Erro ao registrar compra', 'error');
  };

  return (
    <Sheet open onClose={onFechar} title="Nova Compra" footer={<Button variant="primary" loading={salvando} onClick={salvar}>Registrar Compra</Button>}>
      <div className="crm-form-field">
        <label className="crm-form-label">Fornecedor (opcional)</label>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
          <select className="crm-select" value={form.fornecedor_id} onChange={(e) => setForm({ ...form, fornecedor_id: e.target.value })} style={{ flex: 1 }}>
            <option value="">—</option>
            {fornecedores.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={() => setShowNovoFornecedor(true)}>+ Criar</Button>
        </div>
        {showNovoFornecedor && (
          <div style={{ display: 'flex', gap: 'var(--crm-space-2)', marginTop: 'var(--crm-space-2)' }}>
            <input className="crm-input" placeholder="Nome do fornecedor" value={nomeFornecedor} onChange={(e) => setNomeFornecedor(e.target.value)} />
            <Button size="sm" variant="primary" onClick={criarFornecedor}>Salvar</Button>
          </div>
        )}
      </div>
      <div className="crm-form-field">
        <label className="crm-form-label">Destino</label>
        <select className="crm-select" value={form.localizacao_destino_id} onChange={(e) => setForm({ ...form, localizacao_destino_id: e.target.value })}>
          <option value="">Selecione...</option>
          {locs.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
        </select>
      </div>
      <NovaLocalizacaoInline showToast={showToast} onCriada={(l) => { setLocs([...locs, l]); onLocalizacaoCriada?.(); }} />
      <div className="crm-form-field"><label className="crm-form-label">Documento/NF (opcional)</label><input className="crm-input" value={form.documento_nf} onChange={(e) => setForm({ ...form, documento_nf: e.target.value })} /></div>

      <div className="crm-form-label">Itens</div>
      {itens.map((item, i) => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-2)' }}>
          <div style={{ display: 'flex', gap: 'var(--crm-space-2)', alignItems: 'center' }}>
            <select className="crm-select" value={item.produto_id} onChange={(e) => atualizarItem(i, 'produto_id', e.target.value)} style={{ flex: 2 }}>
              <option value="">Produto...</option>
              {produtos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
            <input className="crm-input" type="number" placeholder="Qtd" value={item.quantidade} onChange={(e) => atualizarItem(i, 'quantidade', e.target.value)} style={{ flex: 1 }} />
            <input className="crm-input" type="number" step="0.01" placeholder="Custo unit." value={item.custo_unitario} onChange={(e) => atualizarItem(i, 'custo_unitario', e.target.value)} style={{ flex: 1 }} />
            <Button size="sm" variant="outline" onClick={() => setCriandoProdutoParaLinha(criandoProdutoParaLinha === i ? null : i)}>+ Criar</Button>
            {itens.length > 1 && <button className="crm-icon-btn" onClick={() => removerItem(i)}>×</button>}
          </div>
          {criandoProdutoParaLinha === i && (
            <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
              <input className="crm-input" placeholder="Nome do novo produto" value={nomeProduto} onChange={(e) => setNomeProduto(e.target.value)} />
              <Button size="sm" variant="primary" onClick={() => criarProduto(i)}>Salvar</Button>
            </div>
          )}
        </div>
      ))}
      <Button size="sm" variant="ghost" icon={faPlus} onClick={adicionarItem}>Adicionar item</Button>

      <div className="crm-form-hint">Total: {fmtBRL(total)}</div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
        <input type="checkbox" checked={form.gerar_despesa} onChange={(e) => setForm({ ...form, gerar_despesa: e.target.checked })} />
        Gerar despesa no Financeiro
      </label>
    </Sheet>
  );
}

// ─── Detalhe do produto ──────────────────────────────────────────────────
function DetalheProduto({ produtoId, onVoltar }) {
  const [produto, setProduto] = useState(null);
  const [movimentacoes, setMovimentacoes] = useState(null);
  const [compras, setCompras] = useState(null);

  const carregar = useCallback(async () => {
    const [p, m, c] = await Promise.all([
      apiEst(`/produtos/${produtoId}`), apiEst(`/movimentacoes?produto_id=${produtoId}`), apiEst(`/compras?produto_id=${produtoId}`),
    ]);
    if (p.status === 'ok') setProduto(p.produto);
    if (m.status === 'ok') setMovimentacoes(m.lista);
    if (c.status === 'ok') setCompras(c.lista);
  }, [produtoId]);

  useEffect(() => { carregar(); }, [carregar]);

  if (!produto) return <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)', marginBottom: 'var(--crm-space-4)' }}>
        <button className="crm-icon-btn" onClick={onVoltar}><FontAwesomeIcon icon={faArrowLeft} /></button>
        <div>
          <h2 style={{ margin: 0, fontSize: 'var(--crm-text-xl)', color: 'var(--crm-text-primary)' }}>{produto.nome}</h2>
          {produto.descricao && <div className="crm-form-hint">{produto.descricao}</div>}
        </div>
        {produto.estoque_baixo && <Badge tone="danger">Estoque baixo</Badge>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-4)' }}>
        <KpiGrid>
          <KpiCard label="Saldo total" value={`${fmtNum(produto.saldo_total)} ${produto.unidade}`} />
          <KpiCard label="Estoque mínimo" value={fmtNum(produto.estoque_minimo)} />
          <KpiCard label="Custo unitário" value={fmtBRL(produto.custo)} />
          <KpiCard label="Tipo de controle" value={produto.tipo_controle === 'RASTREAVEL' ? 'Rastreável' : 'Quantidade'} />
        </KpiGrid>

        <Panel title="Saldo por localização">
          {(produto.saldo_por_localizacao || []).length === 0 ? (
            <div className="crm-form-hint">Sem saldo em nenhuma localização.</div>
          ) : (
            <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
              {produto.saldo_por_localizacao.map((s) => <Badge key={s.localizacao_id} tone="accent">{s.localizacao_nome}: {fmtNum(s.quantidade)}</Badge>)}
            </div>
          )}
        </Panel>

        <Panel title="Últimas movimentações" flush>
          {movimentacoes === null ? <div style={{ padding: 'var(--crm-space-4)' }}><FontAwesomeIcon icon={faSpinner} spin /></div> : movimentacoes.length === 0 ? (
            <EmptyState title="Nenhuma movimentação ainda" />
          ) : (
            <div className="crm-table-wrap">
              <table className="crm-table">
                <thead><tr><th>Tipo</th><th>Quantidade</th><th>Data</th><th>Observação</th></tr></thead>
                <tbody>
                  {movimentacoes.slice(0, 20).map((m) => (
                    <tr key={m.id}><td>{TIPOS_MOVIMENTACAO_LABEL[m.tipo]}</td><td>{fmtNum(m.quantidade)}</td><td>{fmtDate(m.data)}</td><td>{m.observacao || '—'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="Compras relacionadas" flush>
          {compras === null ? <div style={{ padding: 'var(--crm-space-4)' }}><FontAwesomeIcon icon={faSpinner} spin /></div> : compras.length === 0 ? (
            <EmptyState title="Nenhuma compra registrada com este produto" />
          ) : (
            <div className="crm-table-wrap">
              <table className="crm-table">
                <thead><tr><th>Data</th><th>Fornecedor</th><th>NF</th><th>Total</th></tr></thead>
                <tbody>
                  {compras.map((c) => <tr key={c.id}><td>{fmtDate(c.data)}</td><td>{c.fornecedor_nome || '—'}</td><td>{c.documento_nf || '—'}</td><td>{fmtBRL(c.total)}</td></tr>)}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

// ─── Root ────────────────────────────────────────────────────────────────
function Estoque({ showToast }) {
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  if (produtoSelecionado) {
    return <DetalheProduto produtoId={produtoSelecionado} onVoltar={() => setProdutoSelecionado(null)} />;
  }
  return <ListaEstoque onAbrirProduto={setProdutoSelecionado} showToast={showToast} />;
}

export default Estoque;
